import { auth, getActiveAuth } from '../config/firebase'

/**
 * Centralized token management service to prevent race conditions
 * and ensure consistent token refresh behavior across the app.
 */
class TokenService {
  private tokenPromise: Promise<string> | null = null
  private lastToken: string | null = null
  private lastRefresh: number = 0
  private readonly TOKEN_REFRESH_THRESHOLD = 50 * 60 * 1000 // 50 minutes
  private monitoringInterval: NodeJS.Timeout | null = null
  private readonly MONITORING_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes

  /**
   * Get a valid Firebase ID token, refreshing if necessary
   * @param forceRefresh - Force a fresh token regardless of cache
   * @returns Promise<string> - Valid Firebase ID token
   */
  async getToken(forceRefresh = false): Promise<string> {
    const now = Date.now()
    const needsRefresh = forceRefresh || 
                        !this.lastToken || 
                        (now - this.lastRefresh) > this.TOKEN_REFRESH_THRESHOLD

    // Return cached token if still valid
    if (!needsRefresh && this.lastToken) {
      return this.lastToken
    }

    // Deduplicate concurrent token refresh calls
    if (!this.tokenPromise) {
      this.tokenPromise = this.refreshToken()
    }

    try {
      const token = await this.tokenPromise
      this.lastToken = token
      this.lastRefresh = now
      return token
    } finally {
      this.tokenPromise = null
    }
  }

  /**
   * Force refresh the token and update cache
   * @returns Promise<string> - Fresh Firebase ID token
   */
  async forceRefresh(): Promise<string> {
    return this.getToken(true)
  }

  /**
   * Clear cached token (useful for logout)
   */
  clearCache(): void {
    this.lastToken = null
    this.lastRefresh = 0
    this.tokenPromise = null
  }

  /**
   * Check if current token is expired or will expire soon
   * @returns boolean - True if token needs refresh
   */
  needsRefresh(): boolean {
    if (!this.lastToken) return true
    const now = Date.now()
    return (now - this.lastRefresh) > this.TOKEN_REFRESH_THRESHOLD
  }

  /**
   * Get time until token expires (in milliseconds)
   * @returns number - Milliseconds until token expires
   */
  getTimeUntilExpiry(): number {
    if (!this.lastToken) return 0
    const now = Date.now()
    const timeSinceRefresh = now - this.lastRefresh
    return Math.max(0, this.TOKEN_REFRESH_THRESHOLD - timeSinceRefresh)
  }

  /**
   * Start monitoring token expiration and proactively refresh
   */
  startMonitoring(): void {
    if (this.monitoringInterval) {
      return // Already monitoring
    }

    this.monitoringInterval = setInterval(() => {
      this.checkAndRefreshIfNeeded()
    }, this.MONITORING_INTERVAL)
  }

  /**
   * Stop monitoring token expiration
   */
  stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval)
      this.monitoringInterval = null
    }
  }

  /**
   * Check if token needs refresh and refresh proactively
   * @private
   */
  private async checkAndRefreshIfNeeded(): Promise<void> {
    try {
      if (this.needsRefresh()) {
        await this.forceRefresh()
      }
    } catch (error) {
      console.warn('TokenService: Failed to proactively refresh token', error)
    }
  }

  /**
   * Wait for auth state to be ready (currentUser to be set)
   * This handles the case where we just signed in but auth state hasn't updated yet
   * @private
   */
  private async waitForAuthReady(timeoutMs: number = 5000): Promise<any> {
    const activeAuth = getActiveAuth()
    
    // If currentUser already exists, return immediately
    if (activeAuth.currentUser) {
      return activeAuth.currentUser
    }

    // Otherwise, wait for onAuthStateChanged to fire with a non-null user
    return new Promise((resolve, reject) => {
      let isResolved = false
      
      const timeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true
          unsubscribe()
          reject(new Error('Timeout waiting for authentication'))
        }
      }, timeoutMs)

      const { onAuthStateChanged } = require('firebase/auth')
      const unsubscribe = onAuthStateChanged(activeAuth, (user) => {
        // Only resolve on the first non-null user
        if (user && !isResolved) {
          isResolved = true
          clearTimeout(timeout)
          unsubscribe()
          resolve(user)
        }
      }, (error) => {
        // Handle auth state change errors
        if (!isResolved) {
          isResolved = true
          clearTimeout(timeout)
          unsubscribe()
          reject(error)
        }
      })
    })
  }

  /**
   * Internal method to refresh token from Firebase
   * @private
   */
  private async refreshToken(): Promise<string> {
    // Use active auth (default or view-as based on sessionStorage)
    const activeAuth = getActiveAuth()
    const user = activeAuth.currentUser
    if (!user) {
      throw new Error('User not authenticated')
    }

    try {
      // Force refresh to get the freshest possible token
      const token = await user.getIdToken(true)
      
      // Validate token format
      if (!token || typeof token !== 'string' || token.length < 100) {
        throw new Error('Invalid token received from Firebase')
      }

      return token
    } catch (error) {
      console.error('TokenService: Failed to refresh token', error)
      throw new Error('Failed to refresh authentication token')
    }
  }
}

// Export singleton instance
export const tokenService = new TokenService()

// Export class for testing
export { TokenService }
